import * as React from "react";
import {
  Box,
  Button,
  Circle,
  Container,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { FiFile } from "react-icons/fi";
import AnimatedPage from "@components/animated-page";
import Title from "@components/title";
import { useAppSelector } from "@store/hooks";
import { AppDispatch, RootState } from "@store/index";
import Spinner from "@components/spinner";
import PrintPreferences from "@components/print-preferences";
import { getPrintJobsByIds, updatePrintJob } from "@features/print/action";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { formValues } from "../../types/form";
import { UNSAFE_NavigationContext, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { revertAll } from "@features/upload/action";
import useTitle from "@hooks/useTitle";

const BevestigUpload = () => {
  const { isLoading } = useAppSelector((state: RootState) => state.user);
  const { upload } = useAppSelector((state: RootState) => state.upload);
  const { printJobsByIds } = useAppSelector((state: RootState) => state.print);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = i18n.language;

  useTitle(t(("documentTitle.printManagement")))

  useEffect(() => {
    if (upload?.length) {
      (async () => {
        return unwrapResult(
          await dispatch(
            getPrintJobsByIds({
              id: Object.values(upload?.map(({ ID }) => ID)).join(","),
            })
          )
        );
      })();
    }

    return () => {
      dispatch(revertAll());
    };
  }, [dispatch, lang, navigate, t, upload]);

  const [confirmed, setConfirmed] = useState(false);
  const confirmedRef = useRef(false);
  const blockerTxRef = useRef<any>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // In-app navigation blocker via UNSAFE_NavigationContext (react-router v6.3)
  const { navigator } = useContext(UNSAFE_NavigationContext);
  useEffect(() => {
    if (confirmed) return;
    const unblock = (navigator as any).block((tx: any) => {
      blockerTxRef.current = tx;
      onOpen();
    });
    return unblock;
  }, [confirmed, navigator, onOpen]);

  // Browser-level navigation warning (refresh, tab close, address bar)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!confirmedRef.current) {
        e.preventDefault();
        e.returnValue = t("printPreferences.autoConfirm.leaveWarning");
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [t]);

  const handleLeave = () => {
    onClose();
    confirmedRef.current = true;
    setConfirmed(true);
    if (blockerTxRef.current) {
      blockerTxRef.current.retry();
      blockerTxRef.current = null;
    }
  };

  const handleStay = () => {
    onClose();
    blockerTxRef.current = null;
  };

  const handleSubmit = (values: formValues) => {
    confirmedRef.current = true;
    setConfirmed(true);
    const body = printJobsByIds.map(({ ID }) => {
      return { ID: ID, ...values, STATUS: 21 };
    });

    new Promise((resolve, reject) => {
      dispatch(
        updatePrintJob({
          body: body,
        })
      )
        .then(() => resolve(navigate(`/${lang}/${t("router.mijn-wachtrij")}`)))
        .catch((err) => reject(err));
    });
  };

  if (isLoading) return <Spinner />;
  return (
    <>
      <Modal isOpen={isOpen} onClose={handleStay} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("printPreferences.leaveModal.title")}</ModalHeader>
          <ModalBody>
            <Text>{t("printPreferences.autoConfirm.leaveWarning")}</Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant={"primary"} onClick={handleStay}>
              {t("printPreferences.leaveModal.stay")}
            </Button>
            <Button variant={"outline"} onClick={handleLeave}>
              {t("printPreferences.leaveModal.leave")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Box>
        <Title title={t("printManagement.confirmUpload.title")} />
        <AnimatedPage animationType={"FadeInUp"}>
          <Container
            maxW={"container.xl"}
            p={{ base: "22px 16px", md: "45px 32px" }}
          >
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacing={{ base: "20px", md: "40px", lg: "80px" }}
            >
              <Box>
                <Heading
                  mb={{ base: "20px", md: "32px" }}
                  fontSize={"2xl"}
                  fontWeight={"normal"}
                >
                  {t("printManagement.confirmUpload.subTitle")}
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, lg: 2, xl: 3 }}
                  gap={22}
                >
                  {printJobsByIds.map((printJob, index) => (
                    <Box
                      borderWidth={"1px"}
                      borderColor={"custom.gray_300"}
                      p={"16px"}
                      key={index}
                    >
                      <VStack spacing={"22px"} alignItems={"flex-start"}>
                        <VStack
                          spacing={"12px"}
                          alignItems={"flex-start"}
                          w={"full"}
                        >
                          <Circle size={"40px"} bg={"custom.brand_primary"}>
                            <Icon
                              as={FiFile}
                              boxSize={"20px"}
                              color={"custom.brand_primaryLight"}
                            />
                          </Circle>
                          <Text wordBreak={"break-word"}>
                            {printJob?.DOCUMENT_NAME}
                          </Text>
                        </VStack>
                        ))
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
              <PrintPreferences onSubmit={handleSubmit} />
            </SimpleGrid>
          </Container>
        </AnimatedPage>
      </Box>
    </>
  );
};

export default BevestigUpload;
